/**
 * What an image file really is, read from its first bytes.
 *
 * The browser's `File.type` comes from the file name, and some Android pickers
 * hand over none at all — a camera JPEG then arrived as "application/octet-
 * stream" and was stored unshrunk, its EXIF unhandled.
 */

function startsWith(bytes: Uint8Array, signature: number[], at = 0): boolean {
    return signature.every((value, index) => bytes[at + index] === value);
}

function ascii(bytes: Uint8Array, from: number, to: number): string {
    return String.fromCharCode(...bytes.subarray(from, to));
}

/** HEIF brands (iPhone photos and their kin); AVIF is the AV1 one. */
const HEIF_BRANDS = new Set([
    'heic',
    'heix',
    'hevc',
    'hevx',
    'heim',
    'heis',
    'mif1',
    'msf1'
]);

/** The mime type the bytes say, or null when they are no image this knows. */
export function sniffImageType(bytes: Uint8Array): string | null {
    if (startsWith(bytes, [0xff, 0xd8, 0xff])) {
        return 'image/jpeg';
    }
    if (startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        return 'image/png';
    }
    if (ascii(bytes, 0, 4) === 'GIF8') {
        return 'image/gif';
    }
    if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') {
        return 'image/webp';
    }
    if (ascii(bytes, 0, 2) === 'BM') {
        return 'image/bmp';
    }
    if (ascii(bytes, 4, 8) === 'ftyp') {
        // The major brand, then the compatible ones up to the box's end. An
        // AVIF may well name the generic HEIF 'mif1' first and 'avif' only
        // among the compatible — it is still an AVIF, and may be transparent.
        const boxEnd = Math.min(
            bytes.length,
            ((bytes[0] << 24) |
                (bytes[1] << 16) |
                (bytes[2] << 8) |
                bytes[3]) >>>
                0
        );
        const brands = [ascii(bytes, 8, 12)];
        for (let at = 16; at + 4 <= boxEnd; at += 4) {
            brands.push(ascii(bytes, at, at + 4));
        }
        if (brands.some((brand) => brand === 'avif' || brand === 'avis')) {
            return 'image/avif';
        }
        if (HEIF_BRANDS.has(brands[0])) {
            return 'image/heic';
        }
    }
    return null;
}

/**
 * Formats that can carry transparency. A JPEG cannot, so these are the ones
 * whose transparent ground a JPEG re-encode would turn black.
 */
export function mayHaveAlpha(mimeType: string): boolean {
    return ['image/png', 'image/webp', 'image/gif', 'image/avif'].includes(
        mimeType
    );
}

/**
 * Formats every consumer of a stored picture handles: the browser, the
 * server's previews, and the PDF (react-pdf embeds JPEG and PNG only).
 */
export function isPortableImage(mimeType: string): boolean {
    return mimeType === 'image/jpeg' || mimeType === 'image/png';
}
